import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import { compose } from "recompose";
import {
    withStyles, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, MenuItem, ButtonGroup, Button, Typography
} from "@material-ui/core";
import CancelAcceptForm from "../../Common/CancelAcceptForm";
import t from "../../../languages/pl";
import { notArrayOrEmpty } from '../../../utils/helpers';
import EmailTextField from './../../Common/InputFields/EmailTextField';
import ChangePasswordExpirationDialog from './../../Common/ChangePasswordExpirationDialog';
import ConfirmationDialog from '../../Common/ConfirmationDialog';
import { indefinitePasswordExpiration } from "../../../utils/staticData";
import guid from './../../../utils/guid';
import { isEmail } from './../../../utils/helpers';

const useStyles = theme => ({
    form: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    row: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },
    textfield: {
        margin: theme.spacing(1),
        width: 200
    },
    groupSelection: {
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start"
    },
    sectionTitle: {
        marginTop: theme.spacing(1),
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        width: 416
    },
    checkbox: {
        color: "grey"
    },
    requiredFields: {
        fontSize: 10,
        marginTop: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        color: "red",
    },
    buttonGroupSpacing: {
        marginTop: theme.spacing(2)
    }
});

const initialState = {
    operator_email: '',
    operator_grupa: -1,
    operator_login: '',
    operator_nazwa: '',
    operator_telefon: '',
    operator_waznosc_hasla: 30
};

const OperatorsDialog = props => {
    const { classes, open, onClose, groups, onPasswordReset, isEdit = false, inputData, onPasswordExpirationChange,
        onSubmit, onEdit, onOperatorDelete } = props;
    const [passwordExpirationOpen, setPasswordExpirationOpen] = useState(false);
    const [deleteConfOpen, setDeleteConfOpen] = useState(false);
    const [state, setState] = useState(initialState);
    const [passwordResetConfOpen, setPasswordResetConfOpen] = useState(false);
    const [emptyEmail, setEmptyEmail] = useState(false);
    const [emptyLogin, setEmptyLogin] = useState(false);
    const [emptyName, setEmptyName] = useState(false);
    const [emptyPhone, setEmptyPhone] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [isEmailCorrect , setIsEmailCorrect] = useState(false);

    useEffect(() => {
        state.operator_email ? setEmptyEmail(false) : setEmptyEmail(true);
        state.operator_email !== "" && isEmail(state.operator_email) ? setIsEmailCorrect(true) : setIsEmailCorrect(false)
    }, [state.operator_email]);

    useEffect(() => {
        state.operator_login ? setEmptyLogin(false) : setEmptyLogin(true);
    }, [state.operator_login]);

    useEffect(() => {
        state.operator_nazwa ? setEmptyName(false) : setEmptyName(true);
    }, [state.operator_nazwa]);

    useEffect(() => {
        state.operator_telefon ? setEmptyPhone(false) : setEmptyPhone(true);
    }, [state.operator_telefon]);


    useEffect(() => {
        if (!inputData)
            return;
        setState(inputData);
    }, [inputData]);

    const handleChange = e => {
        e.persist();
        setState(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value
        }));
    };

    const handleEmailChange = e => {
        e.persist();
        setState(prevState => ({ ...prevState, operator_email: e.target.value }));
    };

    const handleClose = () => {
        setShowWarning(false);
        onClose(initialState);
    };

    const handleSubmit = () => {
        if (!emptyEmail && !emptyLogin && !emptyName && !emptyPhone && isEmailCorrect) {
            setShowWarning(false);
            if (isEdit) {
                onEdit(state, initialState);
            } else {
                onSubmit(state, initialState);
            }
        } else {
            setShowWarning(true);
        }
    };

    const handleOperatorDelete = (id) => {
        setDeleteConfOpen(false);
        onOperatorDelete(id, initialState);
    };

    const handlePasswordExpirationChange = (value) => {
        setPasswordExpirationOpen(false);
        onPasswordExpirationChange(inputData.operator_id, value);
    };

    const printOptions = options => {
        if (notArrayOrEmpty(options))
            return;
        return options.map(option => (
            <MenuItem key={guid()} value={option.id}>{option.name}</MenuItem>
        ));
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth= "sm"
        >
            <DialogTitle>
                {t.operator}
            </DialogTitle>
            <DialogContent>
                <form className={classes.form}>
                    <div className={classes.row}>
                        <TextField
                            className={classes.textfield}
                            label={t.operatorLogin}
                            value={state.operator_login}
                            name="operator_login"
                            onChange={handleChange}
                            required
                        />
                        <EmailTextField
                            className={classes.textfield}
                            label={t.passwordLabel}
                            value={state.operator_email === null ? '' : state.operator_email}
                            isRequired
                            onChange={handleEmailChange}
                        />
                    </div>
                    <div className={classes.row}>
                        <TextField
                            className={classes.textfield}
                            name="operator_nazwa"
                            label={t.firstAndLastName}
                            value={state.operator_nazwa}
                            onChange={handleChange}
                            required
                        />
                        <TextField
                            className={classes.textfield}
                            name="operator_telefon"
                            label={t.phone}
                            value={state.operator_telefon}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className={classes.row}>
                        {
                            !isEdit &&
                            <TextField
                                label={t.passwordExpiration}
                                className={classes.textfield}
                                select
                                defaultValue={30}
                                name="operator_waznosc_hasla"
                                onChange={handleChange}
                            >
                                <MenuItem value={30}>30&nbsp;{t.days}</MenuItem>
                                <MenuItem value={60}>60&nbsp;{t.days}</MenuItem>
                                <MenuItem value={90}>90&nbsp;{t.days}</MenuItem>
                                <MenuItem value={indefinitePasswordExpiration}>{t.noExpiration}</MenuItem>
                            </TextField>
                        }
                    </div>
                    <div className={classes.sectionTitle}>
                        <TextField
                            label={t.group}
                            fullWidth
                            select
                            name="operator_grupa"
                            value={state.operator_grupa}
                            onChange={handleChange}
                        >
                            <MenuItem value={-1}><em>{t.all}</em></MenuItem>
                            {printOptions(groups)}
                        </TextField>
                    </div>
                </form>
                <Typography
                    variant="body1"
                    className={classes.requiredFields}
                    style={{ display: showWarning ? "block" : "none" }}
                >
                    *{t.reqiredFieldsNotFilled}
                </Typography>
            </DialogContent>
            <DialogActions
                className={classes.buttonGroupSpacing}
            >
                {
                    isEdit &&
                    <>
                        <ButtonGroup
                            variant="contained"
                            color="primary"
                        >
                            <Button
                                onClick={() => setPasswordExpirationOpen(true)}
                            >
                                {t.changePasswordExpiration}
                            </Button>
                            <Button
                                onClick={() => setPasswordResetConfOpen(true)}
                            >
                                {t.resetPassword}
                            </Button>
                        </ButtonGroup>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setDeleteConfOpen(true)}
                        >
                            {t.delete}
                        </Button>
                        <ChangePasswordExpirationDialog
                            open={passwordExpirationOpen}
                            onClose={() => setPasswordExpirationOpen(false)}
                            onSubmit={handlePasswordExpirationChange}
                        />
                        <ConfirmationDialog
                            open={passwordResetConfOpen}
                            onClose={() => setPasswordResetConfOpen(false)}
                            action={t.changePasswordForThisOperator}
                            id={inputData.operator_id}
                            onSubmit={(id) => onPasswordReset && onPasswordReset(id)}
                        />
                        <ConfirmationDialog
                            open={deleteConfOpen}
                            onClose={() => setDeleteConfOpen(false)}
                            action={t.deleteThisOperator}
                            onSubmit={handleOperatorDelete}
                            id={inputData.operator_id}
                        />
                    </>
                }
                <CancelAcceptForm onCancel={handleClose} onSubmit={handleSubmit} />
            </DialogActions>
        </Dialog>
    );
};

const mapStateToProps = state => ({
    groups: state.filterReducer.groupOptions
});

OperatorsDialog.propTypes = {
    classes: PropTypes.object,
    open: PropTypes.bool,
    isEdit: PropTypes.bool,
    inputData: PropTypes.object,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    onEdit: PropTypes.func,
    onPasswordReset: PropTypes.func,
    onPasswordExpirationChange: PropTypes.func,
    onOperatorDelete: PropTypes.func,
    //redux
    groups: PropTypes.array
};

export default compose(
    withStyles(useStyles),
    connect(
        mapStateToProps
    )
)(OperatorsDialog);

